import { useEffect, useRef, useState } from "react";

import type {
  ProtectedFieldSelection,
  ProtectedVaultRecord,
  ReunificationCard,
  ReunificationCardKind,
} from "@safesphere/contracts";
import { useI18n } from "../i18n/I18nProvider.js";
import type { QuickPlanData } from "./familyRepository.js";
import {
  availableProtectedFields,
  availableQuickFields,
  buildReunificationCard,
  cardAccessibleText,
  cardPrintHtml,
  communicationTemplates,
  copyCardText,
  shareCardText,
} from "./communicationCards.js";

interface Props {
  readonly quick: QuickPlanData;
  readonly protectedRecords: readonly ProtectedVaultRecord[];
  readonly vaultUnlocked: boolean;
}

const cardKinds: readonly ReunificationCardKind[] = [
  "FAMILY_STATUS",
  "OUT_OF_AREA_CONTACT",
  "MEETING_POINT",
  "CHILD_SUPPORT",
  "OLDER_ADULT_SUPPORT",
  "PET_REUNIFICATION",
  "COMMUNICATION_TEMPLATE",
];

export function ReunificationCardsPanel({ quick, protectedRecords, vaultUnlocked }: Props) {
  const { locale, t } = useI18n();
  const [kind, setKind] = useState<ReunificationCardKind>("FAMILY_STATUS");
  const [quickIds, setQuickIds] = useState<readonly string[]>([]);
  const [protectedSelections, setProtectedSelections] = useState<
    readonly ProtectedFieldSelection[]
  >([]);
  const [confirmed, setConfirmed] = useState(false);
  const [template, setTemplate] = useState(communicationTemplates(locale)[0]?.text ?? "");
  const [preview, setPreview] = useState<ReunificationCard>();
  const [actionStatus, setActionStatus] = useState("");
  const previewHeading = useRef<HTMLHeadingElement>(null);
  const quickFields = availableQuickFields(quick, locale);
  const protectedFields = vaultUnlocked ? availableProtectedFields(protectedRecords, locale) : [];

  useEffect(() => {
    if (!vaultUnlocked) {
      setProtectedSelections([]);
      setConfirmed(false);
      setPreview((current) => (current?.visibility === "INCLUDES_SENSITIVE" ? undefined : current));
    }
  }, [vaultUnlocked]);

  useEffect(() => {
    setTemplate(communicationTemplates(locale)[0]?.text ?? "");
    setPreview(undefined);
  }, [locale]);

  useEffect(() => {
    if (preview !== undefined) previewHeading.current?.focus();
  }, [preview]);

  const toggleProtected = (id: string) => {
    const separator = id.indexOf(":");
    const selection = { recordId: id.slice(0, separator), field: id.slice(separator + 1) };
    setProtectedSelections((current) =>
      current.some((item) => item.recordId === selection.recordId && item.field === selection.field)
        ? current.filter(
            (item) => item.recordId !== selection.recordId || item.field !== selection.field,
          )
        : [...current, selection],
    );
    setConfirmed(false);
    setPreview(undefined);
  };

  const buildPreview = () => {
    if (protectedSelections.length > 0 && !confirmed) {
      setActionStatus(t("cards.confirm_required"));
      return;
    }
    setPreview(
      buildReunificationCard({
        kind,
        locale,
        quick,
        selectedQuickFieldIds: quickIds,
        protectedRecords,
        protectedSelections,
        vaultUnlocked,
        sensitiveVisibilityConfirmed: confirmed,
        ...(kind === "COMMUNICATION_TEMPLATE" ? { editedTemplate: template } : {}),
      }),
    );
    setActionStatus("");
  };

  const copy = async () => {
    if (preview === undefined) return;
    setActionStatus(t(`cards.action.${await copyCardText(cardAccessibleText(preview))}`));
  };

  const share = async () => {
    if (preview === undefined) return;
    setActionStatus(
      t(`cards.action.${await shareCardText(preview.title, cardAccessibleText(preview))}`),
    );
  };

  const exportText = () => {
    if (preview === undefined) return;
    const url = URL.createObjectURL(
      new Blob([cardAccessibleText(preview)], { type: "text/plain" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "safesphere-fictional-card.txt";
    anchor.click();
    URL.revokeObjectURL(url);
    setActionStatus(t("cards.action.EXPORTED"));
  };

  const print = () => {
    if (preview === undefined) return;
    const printWindow = window.open("", "_blank", "noopener,noreferrer");
    if (printWindow === null) {
      setActionStatus(t("cards.action.UNAVAILABLE"));
      return;
    }
    printWindow.document.write(cardPrintHtml(preview));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="cards-builder">
      <p>{t("cards.privacy_default")}</p>
      <fieldset>
        <legend>{t("cards.choose_type")}</legend>
        <div className="cards-kind-grid">
          {cardKinds.map((item) => (
            <button
              aria-pressed={kind === item}
              key={item}
              onClick={() => {
                setKind(item);
                setPreview(undefined);
              }}
              type="button"
            >
              {t(`cards.kind.${item}`)}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend>{t("cards.quick_fields")}</legend>
        {quickFields.map((field) => (
          <label key={field.id}>
            <input
              checked={quickIds.includes(field.id)}
              onChange={() =>
                setQuickIds((current) =>
                  current.includes(field.id)
                    ? current.filter((id) => id !== field.id)
                    : [...current, field.id],
                )
              }
              type="checkbox"
            />
            {field.label}: {field.value}
          </label>
        ))}
      </fieldset>
      <fieldset>
        <legend>{t("cards.protected_fields")}</legend>
        {!vaultUnlocked ? (
          <p>{t("cards.unlock_required")}</p>
        ) : (
          protectedFields.map((field) => (
            <label key={field.id}>
              <input
                checked={protectedSelections.some(
                  (selection) => `${selection.recordId}:${selection.field}` === field.id,
                )}
                onChange={() => toggleProtected(field.id)}
                type="checkbox"
              />
              {field.label}
            </label>
          ))
        )}
      </fieldset>
      {protectedSelections.length > 0 && (
        <label className="cards-sensitive-confirm">
          <input
            checked={confirmed}
            onChange={(event) => {
              setConfirmed(event.currentTarget.checked);
              setPreview(undefined);
            }}
            type="checkbox"
          />
          {t("cards.sensitive_confirm")}
        </label>
      )}
      {kind === "COMMUNICATION_TEMPLATE" && (
        <div className="cards-template">
          <label>
            {t("cards.template_choose")}
            <select onChange={(event) => setTemplate(event.currentTarget.value)} value={template}>
              {communicationTemplates(locale).map((item) => (
                <option key={item.id} value={item.text}>
                  {t(`cards.template.${item.id}`)}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t("cards.template_edit")}
            <textarea
              onChange={(event) => setTemplate(event.currentTarget.value)}
              rows={4}
              value={template}
            />
          </label>
        </div>
      )}
      <button className="button button--primary" onClick={buildPreview}>
        {t("cards.preview")}
      </button>
      {preview !== undefined && (
        <section
          className={`card-preview card-preview--${preview.visibility.toLowerCase()}`}
          aria-labelledby="card-preview-title"
        >
          <p className="card-fixture-label">{t("cards.fixture")}</p>
          <h3 id="card-preview-title" ref={previewHeading} tabIndex={-1}>
            {preview.title}
          </h3>
          <p className="card-visibility">{t(`cards.visibility.${preview.visibility}`)}</p>
          <dl>
            {preview.fields.map((field) => (
              <div key={field.id}>
                <dt>{field.label}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
          <div className="family-actions">
            <button className="button" onClick={() => void copy()}>
              {t("cards.copy")}
            </button>
            <button className="button" onClick={() => void share()}>
              {t("cards.share")}
            </button>
            <button className="button" onClick={exportText}>
              {t("cards.export_text")}
            </button>
            <button className="button" onClick={print}>
              {t("cards.print")}
            </button>
          </div>
        </section>
      )}
      {actionStatus.length > 0 && <p aria-live="polite">{actionStatus}</p>}
    </div>
  );
}
